import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InputV2 } from '../InputV2/InputV2';
import { useAliasValidation } from '../../hooks/useAliasValidation';
import CollectionsAPI from '../../api/CollectionsAPI';
import { Check, X } from 'lucide-react';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface CatalogSlugInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  currentAlias?: string;
  onValidationChange?: (isValid: boolean) => void;
  helperText?: string;
}

const CatalogSlugInput: React.FC<CatalogSlugInputProps> = ({
  value,
  onChange,
  placeholder = '',
  disabled = false,
  currentAlias,
  onValidationChange,
  helperText,
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(!value);
  const [hasValidated, setHasValidated] = useState(false);

  const { validateAliasFormat, debouncedCheckAvailability, isValidationInProgress, currentAbortController } = useAliasValidation();

  const checkSlug = (alias: string) =>
    CollectionsAPI.checkSlugAvailability(alias, currentAbortController?.signal);

  const isOwnAlias = (text: string) => Boolean(currentAlias && text === currentAlias);

  const markValid = (hasValidatedFlag = true) => {
    setAvailabilityError(null);
    setHasValidated(hasValidatedFlag);
    setIsValid(true);
    if (onValidationChange) {
      onValidationChange(true);
    }
  };

  const markInvalid = () => {
    setIsValid(false);
    if (onValidationChange) {
      onValidationChange(false);
    }
  };

  const validateLinkText = (text: string, clearAvailabilityError: boolean = false): boolean => {
    if (clearAvailabilityError) {
      setAvailabilityError(null);
    }
    if (!text) {
      setError(null);
      return true;
    }
    if (UUID_PATTERN.test(text)) {
      setError(t('admin.collections.form.slug.uuid_not_allowed') || 'This ending cannot look like a catalog ID.');
      return false;
    }
    const validation = validateAliasFormat(text);
    setError(validation.error);
    return validation.isValid;
  };

  const handleLinkTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
    onChange(newText);

    if (newText === value) {
      return;
    }

    setAvailabilityError(null);
    setHasValidated(false);

    if (newText.length === 0) {
      setError(null);
      markValid(false);
      return;
    }

    const formatValid = validateLinkText(newText, true);

    if (!formatValid || newText.length < 3) {
      markInvalid();
      return;
    }

    if (isOwnAlias(newText)) {
      markValid();
      return;
    }

    debouncedCheckAvailability(
      newText,
      (alias: string) => checkSlug(alias),
      (result) => {
        setHasValidated(true);
        if (!result.isAvailable) {
          setAvailabilityError(result.error || 'This alias is not available. Please try a different one.');
          markInvalid();
        } else {
          setAvailabilityError(null);
          setIsValid(true);
          if (onValidationChange) {
            onValidationChange(true);
          }
        }
      }
    );
  };

  useEffect(() => {
    if (!value || value.length === 0) {
      setError(null);
      setHasValidated(false);
      markValid(false);
      return;
    }

    if (value.length < 3) {
      validateLinkText(value, false);
      setHasValidated(false);
      markInvalid();
      return;
    }

    const formatValid = validateLinkText(value, false);
    if (!formatValid) {
      setHasValidated(false);
      markInvalid();
      return;
    }

    if (isOwnAlias(value)) {
      markValid();
      return;
    }

    if (!hasValidated || availabilityError) {
      debouncedCheckAvailability(
        value,
        (alias: string) => checkSlug(alias),
        (result) => {
          setHasValidated(true);
          if (!result.isAvailable) {
            setAvailabilityError(result.error || 'This alias is not available. Please try a different one.');
            markInvalid();
          } else {
            setAvailabilityError(null);
            setIsValid(true);
            if (onValidationChange) {
              onValidationChange(true);
            }
          }
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, currentAlias]);

  useEffect(() => {
    if (!value || value.length === 0) {
      markValid(false);
      return;
    }

    const formatValid = validateLinkText(value, false);
    const isLongEnough = value.length >= 3;
    const isAvailable = !availabilityError;
    const currentIsValid = formatValid && isLongEnough && isAvailable && !isValidationInProgress;

    setIsValid(currentIsValid);
    if (onValidationChange) {
      onValidationChange(currentIsValid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilityError, isValidationInProgress]);

  const displayBaseUrl = `${window.location.origin}/catalogues/`;
  const showHelper = !error && !availabilityError && !isValidationInProgress;

  return (
    <div className="w-full space-y-2">
      <div className="flex items-center space-x-2">
        <span className="text-gray-500 text-sm whitespace-nowrap">
          {displayBaseUrl}
        </span>
        <div className="flex-1 relative">
          <InputV2
            type="text"
            value={value}
            onChange={handleLinkTextChange}
            placeholder={placeholder}
            disabled={disabled}
            className={`flex-1 ${error || availabilityError ? 'border-red-300 focus:border-red-500' : isValid && value ? 'border-green-300 focus:border-green-500' : ''}`}
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {isValidationInProgress && (
              <div className="w-4 h-4 border-2 border-darker-gold-300 border-t-transparent rounded-full animate-spin"></div>
            )}
            {!isValidationInProgress && value && value.length >= 3 && (
              <>
                {hasValidated && isValid && !error && !availabilityError && (
                  <Check className="w-5 h-5 text-green-500" />
                )}
                {hasValidated && (error || availabilityError) && (
                  <X className="w-5 h-5 text-red-500" />
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {error && (
        <p className="text-red-500 text-xs">{error}</p>
      )}
      {availabilityError && (
        <p className="text-red-500 text-xs">{availabilityError}</p>
      )}
      {showHelper && helperText && (
        <p className="text-xs text-gray-500">
          {helperText}
        </p>
      )}
    </div>
  );
};

export default CatalogSlugInput;
